import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { ActivityReport } from '@/models/activity-report.model';
import { getActivityReport } from '@/services/activity-report.service';
import ActivityReportDetail from './activity-report-detail';
import ActivityReportActions from './activity-report-actions';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect} >
      <Nav.Item eventKey="activity-report-detail">Report Detail</Nav.Item>
      <Nav.Item eventKey="activity-report-actions">Actions</Nav.Item>
    </Nav>
  );
};

const Page = () => {
  const requestedTab = useLocation()?.state?.tab;  
  
  const [activeTab, setActiveTab] = useState('activity-report-detail');
  const [formObject, setFormObject] = useState<ActivityReport|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<ActivityReport> = await getActivityReport(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Activity Report: ${errorModel?.errorMessage}`);
        nav('/activities/reports');
      }
      finally {
        setLoading(false);
      }
    }
  };

  return (
    <Panel
      header={
        <PageHeader title="Activity Report Detail">
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/activities'>Activities</Breadcrumb.Item>
            <Breadcrumb.Item href='/activities/reports'>Reports</Breadcrumb.Item>
            <Breadcrumb.Item active>Detail</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" 
        active={activeTab} 
        onSelect={key => setActiveTab(key)} 
        newMode={false} 
      />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'activity-report-detail' && <ActivityReportDetail formObject={formObject}/>}
          {activeTab == 'activity-report-actions' && <ActivityReportActions formObject={formObject}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
